import React from "react";
import PropTypes from "prop-types";
import ContentPlayer from "arena/components/ContentPlayer";
import ContentBody from "arena/components/ContentBody";
import ErrorScreen from "core/components/ErrorScreen";
import { CloseRouteDialogButton } from "arena/components/RouteDialog";
import { ChannelQuery, ContentQuery } from "arena/queries";
import { useQuery } from "@apollo/client";
import ReactGA from "react-ga4";
import { createEvent } from "core/utils/tracker";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import NoMatchScreen from "arena/components/NoMatchScreen";
import ContentHelmet from "arena/components/ContentHelmet";
import { useArena } from "core/components/ArenaProvider";
import Box from "@mui/material/Box";
import ContentLayout from "arena/components/ContentLayout";
import RelatedContent from "arena/components/RelatedContent";
import RelatedContentBannerSlot from "arena/components/RelatedContentBannerSlot";
import useIsMobile from "arena/hooks/useIsMobile";
import { useTranslation } from "react-i18next";

const CHANNEL = "CHANNEL";
const CHANNEL_ON_SITE = "CHANNEL_ON_SITE";
const VIDEO = "VIDEO";

export default function Channel({ params: { contentId, slug } }) {
  const { t } = useTranslation("arena");
  const isMobile = useIsMobile();
  const arena = useArena();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const productId = searchParams.get("productId");

  const onCompleted = (data) => {
    const resolvedContent = data?.content || data?.channel;

    if (resolvedContent) {
      ReactGA.send({
        hitType: "pageview",
        page: location.pathname,
        title: resolvedContent.title,
      });

      createEvent("page", "load");
    }
  };

  const {
    data: dataByContentId,
    loading: loadingByContentId,
    error: errorByContentId,
    refetch: refetchByContentId,
  } = useQuery(ContentQuery, {
    variables: contentId ? { contentId } : undefined,
    skip: !contentId,
    onCompleted,
  });

  const {
    data: dataBySlug,
    loading: loadingBySlug,
    error: errorBySlug,
    refetch: refetchBySlug,
  } = useQuery(ChannelQuery, {
    variables: slug ? { slug } : undefined,
    skip: !slug,
    onCompleted,
  });

  const content = dataBySlug?.channel || dataByContentId?.content;
  const loading = loadingByContentId || loadingBySlug;
  const error = errorByContentId || errorBySlug;
  const refetch =
    (contentId ? refetchByContentId : refetchBySlug) ||
    refetchBySlug ||
    refetchByContentId;
  const isLegacyChannelRoute = Boolean(contentId);
  const resolvedContentId = content?.id || contentId;

  if (error) {
    return <ErrorScreen error={error} />;
  }

  if (!content && !loading) {
    return <NoMatchScreen />;
  }

  if (
    isLegacyChannelRoute &&
    content?.siteUrl &&
    location.pathname !== content.siteUrl
  ) {
    return <Navigate to={`${content.siteUrl}${location.search}`} replace />;
  }

  if (content && content.type !== CHANNEL) {
    return (
      <Navigate
        to={`${content.siteUrl.replace(
          /^\/channel\//,
          `/${content.type.toLowerCase()}/`
        )}${location.search}`}
        replace
      />
    );
  }

  const relatedTabEnabled =
    !arena?.hideRelatedVideoOnStream && (!!content || !loading);
  const showMobileRelated = isMobile && !!content && relatedTabEnabled;
  const showSidebarBanner =
    !isMobile &&
    content?.isAdvEnabled &&
    relatedTabEnabled &&
    content?.rightholder?.id;

  const sidebarHeader = showSidebarBanner ? (
    <RelatedContentBannerSlot
      contentId={resolvedContentId}
      rightholderId={content.rightholder.id}
      place={CHANNEL}
      variant="sidebar"
    />
  ) : null;

  const tabsSchema = {
    RELATED_VIDEO: {
      label: t("video.tabsSchema.relatedVideo"),
      component: (
        <RelatedContent
          type={VIDEO}
          limit={6}
          selectedContentId={resolvedContentId}
          relatedContentId={resolvedContentId}
          productId={productId}
          contentId={resolvedContentId}
          rightholderId={content?.rightholder?.id}
          place={CHANNEL}
          isAdvEnabled={!isMobile && content?.isAdvEnabled}
        />
      ),
      enabled: relatedTabEnabled,
    },
  };

  return (
    <Box data-testid="Channel" key={resolvedContentId || slug}>
      <ContentHelmet content={content} arena={arena} />

      <ContentLayout
        tabsSchema={content ? tabsSchema : {}}
        sidebarHeader={sidebarHeader}
      >
        <ContentPlayer
          sponsorCornerBannerPlace={CHANNEL_ON_SITE}
          sponsorInStreamBannerPlace={CHANNEL_ON_SITE}
          sponsorPlayerLogoPlace={CHANNEL_ON_SITE}
          sponsorPlayerButtonPlace={CHANNEL_ON_SITE}
          sponsorVideoPlace={CHANNEL_ON_SITE}
          hideTitle
          autoPlay
          content={content}
          loading={loading}
          refetch={refetch}
          closeRouteDialogButton={<CloseRouteDialogButton />}
        />

        <Box
          sx={{
            position: { xs: "relative", md: "static" },
            // minHeight: { xs: 500, md: "auto" },
            display: { xs: "flex", md: "block" },
            flexDirection: "column",
            gap: { xs: 2, md: 0 },
          }}
        >
          <ContentBody content={content} loading={loading} />
        </Box>
      </ContentLayout>

      {showMobileRelated && (
        <RelatedContent
          type={VIDEO}
          limit={6}
          selectedContentId={resolvedContentId}
          relatedContentId={resolvedContentId}
          productId={productId}
          isMobile
          title={t("video.tabsSchema.relatedVideo")}
          contentId={resolvedContentId}
          rightholderId={content?.rightholder?.id}
          place={CHANNEL}
          isAdvEnabled={content?.isAdvEnabled}
        />
      )}
    </Box>
  );
}

Channel.propTypes = {
  params: PropTypes.shape({
    contentId: PropTypes.string,
    slug: PropTypes.string,
  }),
};
